(function () {
  'use strict';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Judge.me's public review feed (the same one CWC Product Reviews reads); no API key, CORS-enabled
  const REVIEW_FEED_URL = 'https://cdn.judge.me/reviews/reviews_for_widget';
  // The gallery testimonial only shows on desktop; matches the CSS breakpoint
  const DESKTOP_QUERY = '(min-width: 992px)';

  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.cwcReady === 'true') return;
    sectionEl.dataset.cwcReady = 'true';

    const jsonEl = sectionEl.querySelector('[data-product-json]');
    let product = null;
    try {
      product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
    } catch (error) {
      product = null;
    }

    const form = sectionEl.querySelector('.cwc_product-main__form');

    initGallery(sectionEl);
    if (product) initVariants(sectionEl, product, form);
    if (form) initQuantity(form);
    if (form) initAddToCart(sectionEl, form);
    initSticky(sectionEl, form);
    initTestimonial(sectionEl);
    initDeliveryEstimate(sectionEl);
  }

  /* ---------- Gallery ---------- */

  function initGallery(sectionEl) {
    const track = sectionEl.querySelector('.cwc_product-main__track');
    if (!track) return;

    const slides = Array.from(track.querySelectorAll('.cwc_product-main__slide'));
    const thumbs = Array.from(sectionEl.querySelectorAll('.cwc_product-main__thumb'));
    const dots = Array.from(sectionEl.querySelectorAll('.cwc_product-main__dot'));
    if (slides.length < 2) return;

    function setActive(index) {
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('cwc_product-main__thumb--active', i === index);
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('cwc_product-main__dot--active', i === index);
      });
    }

    function goTo(index) {
      const slide = slides[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      setActive(index);
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(parseInt(thumb.dataset.index, 10) || 0);
      });
    });

    let frame = null;
    track.addEventListener('scroll', function () {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        frame = null;
        const width = track.clientWidth || 1;
        setActive(Math.round(track.scrollLeft / width));
      });
    }, { passive: true });

    window.addEventListener('resize', function () {
      const active = thumbs.findIndex(function (t) { return t.classList.contains('cwc_product-main__thumb--active'); });
      const index = active > -1 ? active : Math.round(track.scrollLeft / (track.clientWidth || 1));
      const slide = slides[index];
      if (slide) track.scrollLeft = slide.offsetLeft - track.offsetLeft;
    });

    sectionEl.cwcGalleryGoToMedia = function (mediaId) {
      const index = slides.findIndex(function (s) { return s.dataset.mediaId === String(mediaId); });
      if (index > -1) goTo(index);
    };
  }

  /* ---------- Variants ---------- */

  function initVariants(sectionEl, product, form) {
    const fieldsets = Array.from(sectionEl.querySelectorAll('.cwc_product-main__option'));
    if (!fieldsets.length || !product.variants) return;

    const idInput = form ? form.querySelector('[data-variant-input]') : null;

    function selectedOptions() {
      return fieldsets.map(function (fieldset) {
        const active = fieldset.querySelector('.cwc_product-main__option-button--active');
        return active ? active.dataset.value : null;
      });
    }

    function findVariant(options) {
      return product.variants.find(function (variant) {
        return variant.options.every(function (opt, i) { return opt === options[i]; });
      });
    }

    function markAvailability(options) {
      fieldsets.forEach(function (fieldset, optionIndex) {
        fieldset.querySelectorAll('.cwc_product-main__option-button').forEach(function (button) {
          const test = options.slice();
          test[optionIndex] = button.dataset.value;
          const match = findVariant(test);
          button.classList.toggle('cwc_product-main__option-button--unavailable', !match || !match.available);
        });
      });
    }

    fieldsets.forEach(function (fieldset) {
      fieldset.addEventListener('click', function (event) {
        const button = event.target.closest('.cwc_product-main__option-button');
        if (!button) return;

        fieldset.querySelectorAll('.cwc_product-main__option-button').forEach(function (b) {
          const isActive = b === button;
          b.classList.toggle('cwc_product-main__option-button--active', isActive);
          b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        const options = selectedOptions();
        const variant = findVariant(options);
        markAvailability(options);
        updateVariant(sectionEl, variant, idInput);
      });
    });

    markAvailability(selectedOptions());
  }

  function updateVariant(sectionEl, variant, idInput) {
    const format = sectionEl.dataset.moneyFormat;
    const available = !!(variant && variant.available);

    if (idInput && variant) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const price = sectionEl.querySelector('[data-price]');
    const compare = sectionEl.querySelector('[data-compare-price]');
    if (price && variant) {
      price.textContent = formatMoney(variant.price, format);
      price.classList.toggle('cwc_product-main__price-current--sale', variant.compare_at_price > variant.price);
    }
    if (compare && variant) {
      const onSale = variant.compare_at_price > variant.price;
      compare.hidden = !onSale;
      if (onSale) compare.textContent = formatMoney(variant.compare_at_price, format);
    }

    const stock = sectionEl.querySelector('[data-stock]');
    if (stock) {
      const text = stock.querySelector('[data-stock-text]');
      stock.classList.toggle('cwc_product-main__stock--sold-out', !available);
      if (text) text.textContent = available ? inStockMessage(stock, variant) : stock.dataset.soldOutText;
    }

    const atc = sectionEl.querySelector('[data-atc]');
    if (atc) {
      const label = atc.querySelector('[data-atc-label]');
      const atcPrice = atc.querySelector('[data-atc-price]');
      atc.disabled = !available;
      if (label) label.textContent = available ? atc.dataset.label : atc.dataset.soldOutLabel;
      if (atcPrice) {
        atcPrice.hidden = !available;
        if (variant) atcPrice.textContent = '· ' + formatMoney(variant.price, format);
      }
    }

    // Sticky bar is hidden entirely while the selected variant is sold out
    const stickyBar = sectionEl.querySelector('[data-sticky]');
    if (stickyBar) stickyBar.classList.toggle('cwc_product-main__sticky--sold-out', !available);

    const stickyPrice = sectionEl.querySelector('[data-sticky-price]');
    const stickyButton = sectionEl.querySelector('[data-sticky-button]');
    if (stickyPrice && variant) stickyPrice.textContent = formatMoney(variant.price, format);
    if (stickyButton) {
      stickyButton.disabled = !available;
      stickyButton.textContent = available ? stickyButton.dataset.label : (atc ? atc.dataset.soldOutLabel : 'Sold Out');
    }

    if (variant) {
      if (variant.featured_media && sectionEl.cwcGalleryGoToMedia) {
        sectionEl.cwcGalleryGoToMedia(variant.featured_media.id);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  // "Only [count] left…" when the variant's tracked stock is at or below the threshold
  function inStockMessage(stock, variant) {
    const lowText = stock.dataset.lowStockText;
    const threshold = parseInt(stock.dataset.lowStockThreshold, 10);
    if (!variant || !lowText || isNaN(threshold)) return stock.dataset.inStockText;

    const counts = {};
    (stock.dataset.stockCounts || '').split(',').forEach(function (pair) {
      const parts = pair.split(':');
      if (parts.length === 2) counts[parts[0]] = parseInt(parts[1], 10);
    });

    const count = counts[String(variant.id)];
    if (count > 0 && count <= threshold) return lowText.replace('[count]', count);
    return stock.dataset.inStockText;
  }

  /* ---------- Quantity ---------- */

  function initQuantity(form) {
    const input = form.querySelector('.cwc_product-main__qty-input');
    if (!input) return;

    form.querySelectorAll('[data-qty]').forEach(function (button) {
      button.addEventListener('click', function () {
        const current = parseInt(input.value, 10) || 1;
        const next = button.dataset.qty === 'plus' ? current + 1 : Math.max(1, current - 1);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ---------- Add To Cart ---------- */

  function initAddToCart(sectionEl, form) {
    const button = form.querySelector('[data-atc]');
    const error = form.querySelector('[data-atc-error]');
    const info = sectionEl.querySelector('.cwc_product-main__info');

    form.addEventListener('submit', function (event) {
      // An app (e.g. product options) may take over the submit — leave it alone
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (!button || button.disabled) return;

      const body = new FormData(form);

      // App options rendered outside the form (engraving, gift message) still travel with the line item
      if (info) {
        info.querySelectorAll('[name^="properties["]').forEach(function (field) {
          if (form.contains(field) || field.disabled) return;
          if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
          if (field.type === 'file') {
            if (field.files && field.files[0]) body.append(field.name, field.files[0]);
            return;
          }
          body.append(field.name, field.value);
        });
      }

      button.classList.add('cwc_product-main__atc--loading');
      button.setAttribute('aria-busy', 'true');
      if (error) error.hidden = true;

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || data.message || 'Add to cart failed');
            return data;
          });
        })
        .then(function (item) {
          return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              refreshThemeCart(cart, item);
              flashAdded(button);
            });
        })
        .catch(function (err) {
          if (error) {
            error.textContent = err.message;
            error.hidden = false;
          }
          document.dispatchEvent(new CustomEvent('cart:error', { detail: { errorMessage: err.message } }));
        })
        .finally(function () {
          button.classList.remove('cwc_product-main__atc--loading');
          button.removeAttribute('aria-busy');
        });
    });
  }

  function flashAdded(button) {
    const label = button.querySelector('[data-atc-label]');
    if (!label) return;
    const original = label.textContent;
    label.textContent = 'Added ✓';
    button.classList.add('cwc_product-main__atc--added');
    setTimeout(function () {
      label.textContent = original;
      button.classList.remove('cwc_product-main__atc--added');
    }, 2000);
  }

  // Same hand-off as CWC_product_card.js so the theme's cart count + quick cart refresh and open
  function refreshThemeCart(cart, item) {
    if (window.theme) window.theme.cartData = cart;
    if (typeof window.emit === 'function') window.emit('cart:updated', { cart: cart });

    document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: item } }));
  }

  /* ---------- Sticky Add To Cart ---------- */

  function initSticky(sectionEl, form) {
    const bar = sectionEl.querySelector('[data-sticky]');
    const atc = sectionEl.querySelector('[data-atc]');
    if (!bar || !atc || !form || !('IntersectionObserver' in window)) return;

    bar.hidden = false;

    const stickyButton = bar.querySelector('[data-sticky-button]');
    if (stickyButton) {
      stickyButton.addEventListener('click', function () {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(atc);
        } else {
          atc.click();
        }
      });
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Show only once the main button has scrolled up past the top, not before reaching it
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('cwc_product-main__sticky--visible', passed);
        bar.setAttribute('aria-hidden', passed ? 'false' : 'true');
      });
    });
    observer.observe(atc);
    bar.setAttribute('aria-hidden', 'true');
  }

  /* ---------- Helpers ---------- */

  function formatMoney(cents, format) {
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    const fmt = format || '${{amount}}';
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const amount = parseInt(value, 10) / 100;

    function withDelimiters(number, decimals, thousands, decimal) {
      const fixed = number.toFixed(decimals).split('.');
      const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fixed[1] ? whole + decimal + fixed[1] : whole;
    }

    let formatted;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        formatted = withDelimiters(amount, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(amount, 0, '.', ',');
        break;
      default:
        formatted = withDelimiters(amount, 2, ',', '.');
    }
    return match ? fmt.replace(match[0], formatted) : formatted;
  }

  /* ---------- Delivery Estimate ---------- */

  // Dates are plain calendar days in store (Pacific) time, held as UTC midnights so the math
  // never shifts with the shopper's own time zone
  const STORE_TIME_ZONE = 'America/Los_Angeles';

  function initDeliveryEstimate(sectionEl) {
    const estimate = sectionEl.querySelector('[data-delivery-estimate]');
    if (!estimate) return;
    const dateEl = estimate.querySelector('[data-estimate-date]');
    if (!dateEl) return;

    const data = estimate.dataset;
    const closed = (data.closedDates || '').split(',').filter(function (date) {
      return /^\d{4}-\d{2}-\d{2}$/.test(date);
    });
    const isBusinessDay = function (date) {
      const day = date.getUTCDay();
      return day !== 0 && day !== 6 && closed.indexOf(date.toISOString().slice(0, 10)) === -1;
    };

    // Ordered on a business day before the cutoff: processing counts from today,
    // otherwise from the next business day
    const now = storeNow();
    let start = now.date;
    if (!isBusinessDay(start) || now.hour >= (parseInt(data.cutoffHour, 10) || 0)) {
      start = addBusinessDays(start, 1, isBusinessDay);
    }

    const processingMin = parseInt(data.processingMin, 10) || 0;
    const processingMax = Math.max(parseInt(data.processingMax, 10) || 0, processingMin);
    const transitMin = parseInt(data.transitMin, 10) || 0;
    const transitMax = Math.max(parseInt(data.transitMax, 10) || 0, transitMin);

    const earliest = addBusinessDays(addBusinessDays(start, processingMin, isBusinessDay), transitMin, isBusinessDay);
    const latest = addBusinessDays(addBusinessDays(start, processingMax, isBusinessDay), transitMax, isBusinessDay);

    const format = function (date) {
      return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
    };
    dateEl.textContent = earliest.getTime() === latest.getTime() ? format(latest) : format(earliest) + ' \u2013 ' + format(latest);
    estimate.hidden = false;
  }

  function storeNow() {
    const parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: STORE_TIME_ZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(new Date()).forEach(function (part) {
      parts[part.type] = parseInt(part.value, 10);
    });
    return {
      date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
      hour: parts.hour,
    };
  }

  // Counts forward over business days only; the start day itself never counts
  function addBusinessDays(date, days, isBusinessDay) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (isBusinessDay(result)) added += 1;
    }
    return result;
  }

  /* ---------- Gallery Testimonial ---------- */

  function initTestimonial(sectionEl) {
    const card = sectionEl.querySelector('.cwc_product-main__testimonial[data-product-review]');
    if (!card) return;

    // Card is hidden below desktop, so only fetch once the screen is wide enough
    const query = window.matchMedia(DESKTOP_QUERY);
    if (query.matches) {
      loadProductReview(card);
      return;
    }
    const onChange = function (event) {
      if (!event.matches) return;
      query.removeEventListener('change', onChange);
      loadProductReview(card);
    };
    query.addEventListener('change', onChange);
  }

  function loadProductReview(card) {
    const domain = card.dataset.shopDomain;
    const minRating = parseFloat(card.dataset.minRating) || 4;
    const params = new URLSearchParams({
      url: domain,
      shop_domain: domain,
      platform: 'shopify',
      product_id: card.dataset.productId,
      page: '1',
      per_page: '20',
    });

    fetch(REVIEW_FEED_URL + '?' + params.toString())
      .then(function (response) {
        if (!response.ok) throw new Error('Judge.me feed ' + response.status);
        return response.json();
      })
      .then(function (data) {
        const match = (data.reviews || []).map(normalizeReview).find(function (review) {
          return review.rating >= minRating && review.body && review.author;
        });
        if (match) applyReview(card, match);
      })
      .catch(function () {
        // Feed unreachable: keep the default review
      });
  }

  // Judge.me sends names HTML-escaped and bodies as HTML; reduce both to plain text
  function normalizeReview(review) {
    const decode = function (html) {
      return new DOMParser().parseFromString(html || '', 'text/html').body.textContent.replace(/\s+/g, ' ').trim();
    };
    return {
      rating: parseFloat(review.rating) || 0,
      author: review.is_anonymous_reviewer ? '' : decode(review.reviewer_name),
      verified: review.verified_buyer === true,
      body: decode(review.body_html),
    };
  }

  function applyReview(card, review) {
    const stars = card.querySelector('[data-testimonial-stars]');
    const quote = card.querySelector('[data-testimonial-quote]');
    const name = card.querySelector('[data-testimonial-name]');
    const photo = card.querySelector('[data-testimonial-photo]');
    const verified = card.querySelector('[data-testimonial-verified]');

    if (stars) {
      stars.style.setProperty('--rating', Math.min(review.rating * 20, 100) + '%');
      stars.setAttribute('aria-label', review.rating + ' out of 5 stars');
    }
    if (quote) quote.textContent = '\u201C' + review.body + '\u201D';
    if (name) name.textContent = review.author;
    // The default photo belongs to the default reviewer
    if (photo) photo.remove();
    if (verified) verified.hidden = !review.verified;
    card.hidden = false;
  }

  /* ---------- Init ---------- */

  function initAllSections() {
    document.querySelectorAll('.cwc_product-main').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSections);
  } else {
    initAllSections();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('.cwc_product-main');
    if (section) initSection(section);
  });
})();
